import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { useI18n } from "@/i18n";
import api from "@/lib/api";

function trackInstallOnce() {
  if (localStorage.getItem("pwa_install_tracked")) return;
  localStorage.setItem("pwa_install_tracked", "1");
  api.post("/track/pwa-install").catch(() => {});
}

function isIOS() {
  const ua = window.navigator.userAgent || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS;
}

function isAndroid() {
  return /Android/i.test(window.navigator.userAgent || "");
}

function isStandalone() {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

// Always-visible PWA install button.
// - Chrome/Edge/Android: uses the (early-captured) `beforeinstallprompt` -> NATIVE prompt, no tutorial.
// - iOS / Android-without-event / desktop-without-event: short platform-correct instructions.
export default function InstallPWA({ variant = "desktop" }) {
  const { t } = useI18n();
  const [deferred, setDeferred] = useState(() => window.__deferredInstallPrompt || null);
  const [installed, setInstalled] = useState(false);
  const [showHowto, setShowHowto] = useState(false);
  const ios = isIOS();
  const android = isAndroid();

  useEffect(() => {
    if (isStandalone()) setInstalled(true);
    // Pick up an event that may have fired before this component mounted.
    if (window.__deferredInstallPrompt) setDeferred(window.__deferredInstallPrompt);

    const onInstallable = () => setDeferred(window.__deferredInstallPrompt);
    const onInstalledEvt = () => { setInstalled(true); setDeferred(null); trackInstallOnce(); };
    const onPrompt = (e) => { e.preventDefault(); window.__deferredInstallPrompt = e; setDeferred(e); };

    window.addEventListener("pwa-installable", onInstallable);
    window.addEventListener("pwa-installed", onInstalledEvt);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalledEvt);
    return () => {
      window.removeEventListener("pwa-installable", onInstallable);
      window.removeEventListener("pwa-installed", onInstalledEvt);
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalledEvt);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    const prompt = deferred || window.__deferredInstallPrompt;
    if (prompt) {
      prompt.prompt();
      try {
        const choice = await prompt.userChoice;
        if (choice && choice.outcome === "accepted") setInstalled(true);
      } catch {}
      window.__deferredInstallPrompt = null;
      setDeferred(null);
    } else {
      setShowHowto(true);
    }
  };

  const howtoText = ios ? t("pwa.howto_ios") : android ? t("pwa.howto_android") : t("pwa.howto");

  const btnClass =
    variant === "desktop"
      ? "flex items-center gap-2 text-sm border border-gold/40 text-gold px-4 py-2 hover:bg-gold hover:text-void transition-colors"
      : "flex items-center gap-2 text-gold";

  return (
    <>
      <button onClick={handleClick} data-testid={variant === "mobile" ? "install-pwa-button-mobile" : "install-pwa-button"} className={btnClass}>
        <Download size={16} strokeWidth={1.5} />
        {t("pwa.install")}
      </button>

      {showHowto && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center px-6"
          onClick={() => setShowHowto(false)} data-testid="install-pwa-howto">
          <div className="bg-graphite border border-ink/10 max-w-md w-full p-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl text-ink mb-4 flex items-center gap-2">
              <Download size={20} strokeWidth={1.5} className="text-gold" /> {t("pwa.install")}
            </h3>
            <p className="text-ink-soft leading-relaxed mb-6">{howtoText}</p>
            <button onClick={() => setShowHowto(false)} data-testid="install-pwa-howto-close"
              className="bg-gold text-void px-6 py-2.5 hover:bg-gold-hover transition-colors">
              {t("pwa.close")}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
